import api from "@/services/api";
import { ResourceChannel } from "@/services/resources";
import {
  TemporalDataFilter,
  ResourcesDatafilter,
  TimeUnit,
  AdNetworkFilter,
  TagsDataFilter,
  MarketingHierarchyTagFilter,
  SeparateResourcesFilter,
  MarketingHierarchySelectedEntityFilter,
  payloadToQueryParams,
  DateRangeSelection,
  PKFilter,
  ConversionFilter,
  TemporalPeriodsType,
  YearFilter,
} from "@/services/data_filters";
import {
  useConversions,
  useMetricFilter,
  useDetachedTemporalFilter,
  useDataFilters,
} from "@/store/data_filters";
import {
  useDebouncedFilters,
  useDebouncedDataFilters,
  useDebouncedCalculatedResources,
  useDebouncedConversionGroups,
  useDebouncedDetachedTagFilter,
} from "@/store/debounced_filters";
import { computed, reactive, Ref, ref, toRefs } from "vue";
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/vue-query";
import { useResetPage } from "@/services/utils";
import { ResponseTimeSeriesDatasets } from "@/utils/datasets";
import { QueryFunctionContext } from "@/utils/typing";
import { reactiveComputed } from "@vueuse/core";
import {
  AscendingOrDescending,
  orderingKey,
  TableSortDirection,
} from "@/components/DataPresentation";
import { useAdsQuery } from "@/services/ads";
import {
  PublicDashboardWidgetFilters,
  usePublicDashboardWidgetQueryFilters,
} from "@/services/public_dashboard";
import {
  ComparisonKey,
  MetricFilterSet,
  MetricFormat,
  MetricKey,
} from "@/services/metrics";
import { filterCriteriaFormatter } from "@/utils/filterCriteriaFormatter";
import { Tag } from "./tags";
import {
  CalculatedMetricKey,
  isCalculatedMetric,
} from "@/services/calculated_metrics";

// We're setting the stale time to 15 minutes since we're not really dealing with fresh data,
// and we're upping the gcTime from 5 minutes (default) to 30 minutes. This is to reduce the amount of redundant refetches we do,
// and to improve the UX (cached data displays while background refetch happens)
const MARKETING_STALE_TIME = 15 * 60 * 1000; // 15 minutes
const MARKETING_GC_TIME = 30 * 60 * 1000; // 30 minutes

export const MARKETING_RESOURCE_CHANNELS = [
  ResourceChannel.GOOGLE_ADS,
  ResourceChannel.FACEBOOK_ADS,
  ResourceChannel.SNAPCHAT_ADS,
  ResourceChannel.LINKED_IN_ADS,
  ResourceChannel.XANDR,
  ResourceChannel.BING_ADS,
  ResourceChannel.C_ALT,
  ResourceChannel.TIK_TOK_ADS,
  ResourceChannel.KOBLER,
  ResourceChannel.DV_360,
  ResourceChannel.CM_360,
  ResourceChannel.ADFORM,
  ResourceChannel.GOOGLE_SHEETS,
] as const;

export type MarketingResourceChannel =
  (typeof MARKETING_RESOURCE_CHANNELS)[number];

export const BASE_MARKETING_METRICS = [
  "impressions",
  "clicks",
  "clickthroughRate",
  "costPerClick",
  "conversions",
  "conversionsByConvDate",
  "cost",
  "costPerConversion",
  "costPerMilli",
  "totalClicks",
  "outboundClicks",
  "shares",
  "comments",
  "engagement",
  "value",
  "valueByConvDate",
  "returnOnAdSpend",
  "returnOnAdSpendRatio",
  "returnOnAdSpendByConvDate",
  "returnOnAdSpendRatioByConvDate",
  "initialVideoViews",
  "videoViews25Pct",
  "videoViews50Pct",
  "videoViews75Pct",
  "videoViews100Pct",
  "metaThruplays",
  "youtubeViews",
  "averageVideoWatchTime",
  "costPerInitialVideoView",
  "costPerMetaThruplay",
  "youtubeViewRate",
  "totalVideoWatchTime",
  "videoViews15s",
  "videoViews6s",
  "landingPageViews",
  "storyOpens",
  "costPerVideoViews15s",
  "costPerVideoViews6s",
] as const satisfies MetricKey[];

export const MARKETING_CONVERSION_METRICS = [
  "conversions",
  "value",
  "conversionsByConvDate",
  "valueByConvDate",
  "returnOnAdSpend",
  "returnOnAdSpendRatio",
  "returnOnAdSpendByConvDate",
  "returnOnAdSpendRatioByConvDate",
  "costPerConversion",
] as const satisfies MetricKey[];

export const MARKETING_ADFORM_NONAGR_METRICS = [
  "uniqueReachPercent",
  "uniqueClicksPercent",
] as const satisfies MetricKey[];

export const MARKETING_GOOGLE_ADS_NONAGR_METRICS = [
  "absoluteTopImpressionPercentage",
  "historicalQualityScore",
  "searchBudgetLostImpressionShare",
  "searchImpressionShare",
  "searchRankLostImpressionShare",
  "topImpressionPercentage",
] as const satisfies MetricKey[];

export const MARKETING_NONAGR_METRICS = [
  "reach",
  "frequency",
  "viewability",
  ...MARKETING_ADFORM_NONAGR_METRICS,
  ...MARKETING_GOOGLE_ADS_NONAGR_METRICS,
] as const satisfies MetricKey[];

// The marketing entity endpoints can't order by NAGR metrics — they're fetched
// in a separate follow-up query and are absent from ordering_fields, so an
// ordering request for one is silently dropped server-side (mirrored by
// NAGR_METRICS_DEFAULTS on the backend). Single membership test so the check
// can't fork the next time NAGR semantics change.
export function isNagrMetric(metric: string): boolean {
  return (MARKETING_NONAGR_METRICS as readonly string[]).includes(metric);
}

// First metric in a widget's selection that the marketing endpoints CAN order
// by — the shared replacement for a dead NAGR sort key, used identically by
// the render, CSV-export, template and backend-sanitized paths so they can't
// disagree on the fallback. Calculated-metric keys are skipped: the CSV path
// can't tell a live one from a deleted one, and a deleted key would itself
// produce a silently-dropped ordering.
export function firstSortableMarketingMetric(
  metrics: readonly string[] | undefined,
): string | undefined {
  return metrics?.find(
    (metric) => !isNagrMetric(metric) && !isCalculatedMetric(metric),
  );
}

export type MarketingMetric =
  | (typeof BASE_MARKETING_METRICS)[number]
  | (typeof MARKETING_NONAGR_METRICS)[number]
  | CalculatedMetricKey;
export type MarketingTableMetricKey =
  | MarketingMetric
  | "budget"
  | "biddingStrategy"
  | "qualityScore"
  | "status";
export type MarketingConversionMetric =
  (typeof MARKETING_CONVERSION_METRICS)[number];

export enum MarketingMetricsByComparisonLabel {
  CURRENT = "current",
  PREVIOUS = "previous",
}
export type MarketingMetrics = Record<MarketingMetric, number | null>;

export type ComparisonMarketingMetrics = Record<
  ComparisonKey<MarketingMetric>,
  number | null
>;

export interface MarketingMetricsByComparisonPeriod extends MarketingMetrics {
  label: "current" | "previous";
}

export interface MarketingMetricsByComparison {
  currentPeriod: MarketingMetricsByComparisonPeriod;
  previousPeriod: MarketingMetricsByComparisonPeriod;
}

/**
 * Some marketing performance endpoints allow filtering by the tags
 * of all the various levels of marketing entities. This function
 * is used to create a filter with the correct parameters
 * for the current level,
 * e.g. `useMarketingHierarchyLevelSpecificTags(ref(MarketingHierarchyLevel.CAMPAIGN), ref([1, 2, 3]))`
 * would give you a filter returning data for the campaigns with tags 1, 2 and 3.
 * If `tags` is undefined and `useGlobalFilters` is true it uses the global tag filter.
 */
function useMarketingHierarchyLevelSpecificTags(
  level: Ref<MarketingHierarchyLevel | undefined> | undefined,
  includedTags: Ref<number[]> | undefined,
  excludedTags: Ref<number[]> | undefined,
  includedTagsLogic: Ref<"AND" | "OR"> | undefined,
  useGlobalFilters = true,
) {
  const tagFilters = useDebouncedDetachedTagFilter(
    useGlobalFilters,
    includedTags,
    excludedTags,
    includedTagsLogic,
  );
  return toRefs(
    reactiveComputed(() => {
      const defaultFilters = {
        account_tags__in: null,
        not__account_tags__in: null,
        campaign_tags__in: null,
        not__campaign_tags__in: null,
        ad_group_tags__in: null,
        not__ad_group_tags__in: null,
        ad_tags__in: null,
        not__ad_tags__in: null,
        keyword_tags__in: null,
        not__keyword_tags__in: null,
        query_tags__in: null,
        not__query_tags__in: null,
        tags_logic: null,
      };
      if (!level?.value) {
        return defaultFilters;
      }
      const levelFilterKey = {
        [MarketingHierarchyLevel.ACCOUNT]: "account",
        [MarketingHierarchyLevel.CAMPAIGN]: "campaign",
        [MarketingHierarchyLevel.ADGROUP]: "ad_group",
        [MarketingHierarchyLevel.AD]: "ad",
        [MarketingHierarchyLevel.KEYWORD]: "keyword",
        [MarketingHierarchyLevel.SEARCH_TERM]: "query",
      }[level.value];
      return {
        ...defaultFilters,
        [`${levelFilterKey}_tags__in`]: tagFilters.tags__in,
        [`not__${levelFilterKey}_tags__in`]: tagFilters.not__tags__in,
        tags_logic: tagFilters.tags_logic,
      };
    }),
  );
}

/**
 * Per-widget override for the `separateResources` query param. Dashboard widgets
 * run with `useGlobalFilters = false`, which forces the global separateResources
 * to null; this lets a widget supply its own value (e.g. an account-level chart
 * splitting same-channel accounts into separate series). Returns an empty object
 * when the global filters are in use, so the global value is left untouched.
 */
function useWidgetSeparateResources(
  separateResources: Ref<boolean | undefined> | undefined,
  useGlobalFilters: boolean,
): { separateResources: Ref<boolean | null> } | Record<string, never> {
  // Global path leaves separateResources to the spread global filter; widgets
  // (useGlobalFilters=false) supply their own value, falling back to null.
  // Modelled on useCalculatedResources: a plain object with a computed ref that
  // gates on the value rather than the ref's identity. NOTE: this must be spread
  // AFTER useDebouncedDataFilters([..., "separateResources"], false) in the query
  // key — that emits `separateResources: null` for widgets, and the widget's real
  // value only wins by overwriting it. Reorder the spreads and the null clobbers
  // the widget value (silently reverting to the combined view).
  if (useGlobalFilters || !separateResources) return {};
  return {
    separateResources: computed(() => separateResources.value ?? null),
  };
}

/**
 * Some marketing performance endpoints allow filtering by the PK
 * of all the various levels of marketing entities. This function
 * is used to create a filter with the correct parameters
 * for the current level,
 * e.g. `useSelectedEntities(ref(MarketingHierarchyLevel.CAMPAIGN), ref([1, 2, 3]))`
 * would give you a filter returning data for the campaigns with PK 1, 2 and 3.
 */
export function useSelectedEntities(
  level: Ref<MarketingHierarchyLevel | undefined> | undefined,
  selectedEntities: Ref<number[] | undefined> | undefined,
) {
  return toRefs(
    reactiveComputed(() => {
      const defaultFilters = {
        account_pk__in: null,
        campaign_pk__in: null,
        ad_group_pk__in: null,
        ad_pk__in: null,
        ad_group_keyword_pk__in: null,
        ad_group_query_pk__in: null,
        entity_level: null as string | null,
      };
      let activeFilters;
      const orderedSelectedEntities = selectedEntities?.value
        ? [...selectedEntities.value].sort()
        : undefined;
      const parameter = (payloadToQueryParams(orderedSelectedEntities) ??
        null) as number[] | null;
      switch (level?.value) {
        case MarketingHierarchyLevel.ACCOUNT:
          activeFilters = {
            account_pk__in: parameter,
            entity_level: "account",
          };
          break;
        case MarketingHierarchyLevel.CAMPAIGN:
          activeFilters = {
            campaign_pk__in: parameter,
            entity_level: "campaign",
          };
          break;
        case MarketingHierarchyLevel.ADGROUP:
          activeFilters = {
            ad_group_pk__in: parameter,
            entity_level: "adGroup",
          };
          break;
        case MarketingHierarchyLevel.AD:
          activeFilters = {
            ad_pk__in: parameter,
            entity_level: "ad",
          };
          break;
        case MarketingHierarchyLevel.KEYWORD:
          activeFilters = {
            ad_group_keyword_pk__in: parameter,
            entity_level: "keyword",
          };
          break;
        case MarketingHierarchyLevel.SEARCH_TERM:
          activeFilters = {
            ad_group_query_pk__in: parameter,
            entity_level: "adGroupSearchTerm",
          };
          break;
        default:
          activeFilters = {};
      }
      return { ...defaultFilters, ...activeFilters };
    }),
  );
}

function listMarketingMetricsByComparison({
  queryKey: [, params],
}: QueryFunctionContext<
  TemporalDataFilter &
    ResourcesDatafilter &
    AdNetworkFilter &
    ConversionFilter &
    MarketingHierarchyTagFilter &
    MarketingHierarchySelectedEntityFilter &
    PublicDashboardWidgetFilters
>): Promise<MarketingMetricsByComparison> {
  return api
    .get("/resources/marketing/metrics_compared_to_previous_period/", {
      params,
    })
    .then((response) => response.data);
}

export function useMarketingMetricsByComparisonQuery({
  selectedResourceIds,
  temporalFilter,
  metrics,
  enabled,
  hierarchyLevel,
  tagHierarchyLevel,
  selectedTags,
  includedTags,
  excludedTags,
  includedTagsLogic,
  selectedEntities,
  conversionGroups,
  conversions,
  useGlobalFilters = true,
}: {
  selectedResourceIds?: Ref<number[]>;
  temporalFilter?: Ref<DateRangeSelection>;
  metrics?: Ref<string[]>;
  enabled?: Ref<boolean>;
  hierarchyLevel?: Ref<MarketingHierarchyLevel | undefined>;
  tagHierarchyLevel?: Ref<MarketingHierarchyLevel | undefined>;
  selectedTags?: Ref<number[]>;
  includedTags?: Ref<number[]>;
  excludedTags?: Ref<number[]>;
  includedTagsLogic?: Ref<"AND" | "OR">;
  selectedEntities?: Ref<number[] | undefined>;
  conversionGroups?: Ref<number[]>;
  conversions?: Ref<number[]>;
  useGlobalFilters?: boolean;
} = {}) {
  tagHierarchyLevel ??= hierarchyLevel;
  const queryKey = [
    "marketingMetricsByComparison",
    {
      ...useDebouncedDataFilters(["subcategory"], useGlobalFilters),
      ...useDetachedTemporalFilter(temporalFilter, useGlobalFilters),
      ...useMarketingHierarchyLevelSpecificTags(
        tagHierarchyLevel,
        includedTags ?? selectedTags,
        excludedTags,
        includedTagsLogic,
        useGlobalFilters,
      ),
      ...useDebouncedCalculatedResources(selectedResourceIds, useGlobalFilters),
      ...useSelectedEntities(hierarchyLevel, selectedEntities),
      ...useDebouncedConversionGroups(conversionGroups, useGlobalFilters),
      ...useConversions(conversions, useGlobalFilters),
      ...useMetricFilter(metrics, useGlobalFilters),
      ...usePublicDashboardWidgetQueryFilters(),
    },
  ] as const;
  return reactive(
    useQuery({
      queryKey,
      queryFn: listMarketingMetricsByComparison,
      enabled,
      staleTime: MARKETING_STALE_TIME,
      gcTime: MARKETING_GC_TIME,
    }),
  );
}
export const useMarketingMetricsByComparisonQueryWithMetrics = {
  query: useMarketingMetricsByComparisonQuery,
  metrics: [
    ...BASE_MARKETING_METRICS,
    ...MARKETING_NONAGR_METRICS,
    "qualityScore",
  ] as const,
};

export interface ActiveAdsCountPeriod {
  adsCount: number;
}

export interface ActiveAdsCount {
  currentPeriod: ActiveAdsCountPeriod;
  previousPeriod: ActiveAdsCountPeriod | null;
}

function listActiveAdsCount({
  queryKey: [, params],
}: QueryFunctionContext<
  TemporalDataFilter &
    ResourcesDatafilter &
    MarketingHierarchyTagFilter &
    MarketingHierarchySelectedEntityFilter &
    PublicDashboardWidgetFilters
>): Promise<ActiveAdsCount> {
  return api
    .get("/resources/marketing/active_ads_count/", { params })
    .then((response) => response.data);
}

export function useActiveAdsCountQuery({
  selectedResourceIds,
  temporalFilter,
  enabled,
  hierarchyLevel,
  tagHierarchyLevel,
  selectedTags,
  includedTags,
  excludedTags,
  includedTagsLogic,
  selectedEntities,
  useGlobalFilters = true,
}: {
  selectedResourceIds?: Ref<number[]>;
  temporalFilter?: Ref<DateRangeSelection>;
  enabled?: Ref<boolean>;
  hierarchyLevel?: Ref<MarketingHierarchyLevel | undefined>;
  tagHierarchyLevel?: Ref<MarketingHierarchyLevel | undefined>;
  selectedTags?: Ref<number[]>;
  includedTags?: Ref<number[]>;
  excludedTags?: Ref<number[]>;
  includedTagsLogic?: Ref<"AND" | "OR">;
  selectedEntities?: Ref<number[] | undefined>;
  useGlobalFilters?: boolean;
} = {}) {
  tagHierarchyLevel ??= hierarchyLevel;
  const queryKey = [
    "activeAdsCount",
    {
      ...useDetachedTemporalFilter(temporalFilter, useGlobalFilters),
      ...useMarketingHierarchyLevelSpecificTags(
        tagHierarchyLevel,
        includedTags ?? selectedTags,
        excludedTags,
        includedTagsLogic,
        useGlobalFilters,
      ),
      ...useDebouncedCalculatedResources(selectedResourceIds, useGlobalFilters),
      ...useSelectedEntities(hierarchyLevel, selectedEntities),
      ...usePublicDashboardWidgetQueryFilters(),
    },
  ] as const;
  return reactive(
    useQuery({
      queryKey,
      queryFn: listActiveAdsCount,
      enabled,
      staleTime: MARKETING_STALE_TIME,
      gcTime: MARKETING_GC_TIME,
    }),
  );
}

export const useActiveAdsCountQueryWithMetrics = {
  query: useActiveAdsCountQuery,
  metrics: ["adsCount"] as const,
};

export type AggregatedMarketingMetrics = MarketingMetrics &
  ComparisonMarketingMetrics;

export interface MarketingMetricsByAdGroupData extends MarketingMetrics {
  resource: number;
  platform: MarketingResourceChannel;
  campaignName: string;
  campaignId: string;
  adGroupId: string;
  adGroupName: string;
  device: string;
  subcategory: string;
}

export interface MarketingMetricsByAdGroup {
  labels: string[];
  data: MarketingMetricsByAdGroupData[];
}

export interface MarketingMetricByDateAndPlatformDataset {
  label: string;
  channel: MarketingResourceChannel;
  resource: string | null;
  data: (number | null)[];
}

export interface MarketingMetricByDateAndPlatform {
  labels: string[];
  datasets: MarketingMetricByDateAndPlatformDataset[];
}

export type MarketingMetricsByDateAndPlatform = Record<
  MarketingMetric,
  MarketingMetricByDateAndPlatform
>;

export interface MarketingMetricsByDateAndPlatformWithExtraDetails extends MarketingMetricsByDateAndPlatform {
  latestDate: string | null;
  resourceName: string | null;
  resourceChannel: ResourceChannel | null;
}
export type TemporalMarketingMetricsByDateAndPlatform = Record<
  TemporalPeriodsType,
  MarketingMetricsByDateAndPlatformWithExtraDetails
>;

function listMarketingMetricsByDateAndPlatform({
  queryKey: [, params],
}: QueryFunctionContext<
  {
    timeUnit: TimeUnit;
    filterCriteria?: MetricFilterSet[];
  } & SeparateResourcesFilter &
    TemporalDataFilter &
    ResourcesDatafilter &
    AdNetworkFilter &
    ConversionFilter &
    MarketingHierarchyTagFilter &
    MarketingHierarchySelectedEntityFilter &
    PublicDashboardWidgetFilters
>): Promise<TemporalMarketingMetricsByDateAndPlatform> {
  const filterCriteria = filterCriteriaFormatter(params.filterCriteria);
  return api
    .get("/resources/marketing/metrics_by_date_and_platform/", {
      params: { ...params, ...filterCriteria },
    })
    .then((response) => response.data);
}

export function useMarketingMetricsByDateAndPlatformQuery({
  selectedResourceIds,
  conversionGroups,
  conversions,
  timeUnit = ref(TimeUnit.DAY),
  hierarchyLevel,
  tagHierarchyLevel,
  selectedTags,
  includedTags,
  excludedTags,
  includedTagsLogic,
  enabled,
  selectedEntities,
  temporalFilter,
  useGlobalFilters = true,
  filterCriteria,
  metrics,
  separateResources,
}: {
  selectedResourceIds?: Ref<number[]>;
  conversionGroups?: Ref<number[]>;
  conversions?: Ref<number[]>;
  timeUnit: Ref<TimeUnit>;
  hierarchyLevel?: Ref<MarketingHierarchyLevel | undefined>;
  tagHierarchyLevel?: Ref<MarketingHierarchyLevel | undefined>;
  selectedTags?: Ref<number[]>;
  includedTags?: Ref<number[]>;
  excludedTags?: Ref<number[]>;
  includedTagsLogic?: Ref<"AND" | "OR">;
  enabled?: Ref<boolean>;
  selectedEntities?: Ref<number[] | undefined>;
  temporalFilter?: Ref<DateRangeSelection>;
  useGlobalFilters?: boolean;
  filterCriteria?: Ref<MetricFilterSet[]>;
  metrics?: Ref<string[]>;
  // Per-widget override: when useGlobalFilters is false the global
  // separateResources is forced null, so widgets pass their own value here to
  // split same-channel accounts into separate series.
  separateResources?: Ref<boolean | undefined>;
}) {
  tagHierarchyLevel ??= hierarchyLevel;
  const queryKey = [
    "marketingMetricsByDateAndPlatform",
    {
      ...useDebouncedDataFilters(
        ["subcategory", "separateResources"],
        useGlobalFilters,
      ),
      ...useWidgetSeparateResources(separateResources, useGlobalFilters),
      ...useDetachedTemporalFilter(temporalFilter, useGlobalFilters),
      ...useMarketingHierarchyLevelSpecificTags(
        tagHierarchyLevel,
        includedTags ?? selectedTags,
        excludedTags,
        includedTagsLogic,
        useGlobalFilters,
      ),
      ...useDebouncedCalculatedResources(selectedResourceIds, useGlobalFilters),
      ...useDebouncedConversionGroups(conversionGroups, useGlobalFilters),
      ...useConversions(conversions, useGlobalFilters),
      ...useSelectedEntities(hierarchyLevel, selectedEntities),
      ...usePublicDashboardWidgetQueryFilters(),
      timeUnit,
      filterCriteria,
      ...useMetricFilter(metrics, useGlobalFilters),
    },
  ] as const;
  return reactive(
    useQuery({
      queryKey,
      queryFn: listMarketingMetricsByDateAndPlatform,
      enabled,
      staleTime: MARKETING_STALE_TIME,
      gcTime: MARKETING_GC_TIME,
    }),
  );
}
export const useMarketingMetricsByDateAndPlatformQueryWithMetrics = {
  query: useMarketingMetricsByDateAndPlatformQuery,
  metrics: [
    ...BASE_MARKETING_METRICS,
    ...MARKETING_NONAGR_METRICS,
    "qualityScore",
  ] as const,
};
export type MarketingMetricsByDate = Record<
  MarketingMetric,
  ResponseTimeSeriesDatasets
>;

export interface MarketingMetricsByDateWithExtraDetails extends MarketingMetricsByDate {
  latestDate: string | null;
}

export type TemporalMarketingMetricsByDate = Record<
  TemporalPeriodsType,
  MarketingMetricsByDateWithExtraDetails
>;

export interface SelectedYearCurrentMonthDailySpend {
  selection: {
    labels: string[];
    datasets: [
      {
        label: "actual_cumulative_spend";
        data: (number | null)[];
        period: TemporalPeriodsType;
      },
      {
        label: "projected_cumulative_spend";
        data: (number | null)[];
        period: TemporalPeriodsType;
      },
    ];
  };
  comparison?: null;
}

function listSelectedYearCurrentMonthDailySpend({
  queryKey: [, params],
}: QueryFunctionContext<ResourcesDatafilter>): Promise<SelectedYearCurrentMonthDailySpend> {
  return api
    .get("/resources/marketing/current_month_daily_spend/", { params })
    .then((response) => response.data);
}

export function useSelectedYearCurrentMonthDailySpendQuery() {
  const queryKey = [
    "selectedYearCurrentMonthDailySpend",
    useDataFilters(["includedResources"]),
  ] as const;
  return reactive(
    useQuery({
      queryKey,
      queryFn: listSelectedYearCurrentMonthDailySpend,
      staleTime: MARKETING_STALE_TIME,
      gcTime: MARKETING_GC_TIME,
    }),
  );
}

export interface SelectedYearMonthlySpendByChannelDataset {
  label: string;
  channel: MarketingResourceChannel;
  resource: string | null;
  data: (number | null)[];
  period: TemporalPeriodsType;
}

export interface SelectedYearMonthlySpendByChannel {
  selection: {
    labels: string[];
    datasets: SelectedYearMonthlySpendByChannelDataset[];
  };
  comparison?: null;
}

function listSelectedYearMonthlySpendByChannel({
  queryKey: [, params],
}: QueryFunctionContext<
  SeparateResourcesFilter & ResourcesDatafilter & YearFilter
>): Promise<SelectedYearMonthlySpendByChannel> {
  return api
    .get("/resources/marketing/monthly_spend_by_channel/", { params })
    .then((response) => response.data);
}

export function useSelectedYearMonthlySpendByChannelQuery() {
  const queryKey = [
    "selectedYearMonthlySpendByChannel",
    useDataFilters(["includedResources", "separateResources", "yearFilter"]),
  ] as const;
  return reactive(
    useQuery({
      queryKey,
      queryFn: listSelectedYearMonthlySpendByChannel,
      staleTime: MARKETING_STALE_TIME,
      gcTime: MARKETING_GC_TIME,
    }),
  );
}

export enum MarketingEntityRunningStatus {
  ENABLED = "enabled",
  PAUSED = "paused",
  ISSUES = "issues",
  ENDED = "ended",
  ARCHIVED = "archived",
}

export enum MarketingHierarchyLevel {
  ACCOUNT = "account",
  CAMPAIGN = "campaign",
  ADGROUP = "adGroup",
  AD = "ad",
  KEYWORD = "keyword",
  SEARCH_TERM = "searchTerm",
}

// Per-channel hierarchy names. A channel lists only the levels it renames;
// anything absent uses the generic name. Replaces a switch whose deliberate
// fall-through made each channel inherit the next one's labels.
const LEVEL_NAME_OVERRIDES: Partial<
  Record<ResourceChannel, Partial<Record<MarketingHierarchyLevel, string>>>
> = {
  [ResourceChannel.LINKED_IN_ADS]: {
    [MarketingHierarchyLevel.CAMPAIGN]: "Campaign Group",
    [MarketingHierarchyLevel.ADGROUP]: "Campaign",
  },
  [ResourceChannel.FACEBOOK_ADS]: {
    [MarketingHierarchyLevel.ADGROUP]: "Ad Set",
  },
  [ResourceChannel.SNAPCHAT_ADS]: {
    [MarketingHierarchyLevel.ADGROUP]: "Ad Squad",
  },
  [ResourceChannel.CM_360]: {
    [MarketingHierarchyLevel.ADGROUP]: "Placement",
  },
  [ResourceChannel.DV_360]: {
    [MarketingHierarchyLevel.ADGROUP]: "Insertion Order",
    [MarketingHierarchyLevel.AD]: "Line Item",
  },
  [ResourceChannel.XANDR]: {
    [MarketingHierarchyLevel.CAMPAIGN]: "Insertion Order",
    [MarketingHierarchyLevel.ADGROUP]: "Line Item",
  },
  [ResourceChannel.C_ALT]: {
    [MarketingHierarchyLevel.CAMPAIGN]: "Order",
    [MarketingHierarchyLevel.ADGROUP]: "Line Item",
  },
};

const DEFAULT_LEVEL_NAMES: Partial<Record<MarketingHierarchyLevel, string>> = {
  [MarketingHierarchyLevel.ACCOUNT]: "Account",
  [MarketingHierarchyLevel.CAMPAIGN]: "Campaign",
  [MarketingHierarchyLevel.ADGROUP]: "Ad Group",
  [MarketingHierarchyLevel.AD]: "Ad",
};

export function channelHierarchyLevelName(
  channel: ResourceChannel,
  level: MarketingHierarchyLevel,
): string | undefined {
  return LEVEL_NAME_OVERRIDES[channel]?.[level] ?? DEFAULT_LEVEL_NAMES[level];
}

// Levels whose backend rows carry a network status.
export const STATUS_HIERARCHY_LEVELS = [
  MarketingHierarchyLevel.CAMPAIGN,
  MarketingHierarchyLevel.ADGROUP,
  MarketingHierarchyLevel.AD,
];

export interface MarketingHierarchyEntityBase
  extends MarketingMetrics, ComparisonMarketingMetrics {
  pk: number;
  name: string;
  parentLevelName: string;
  channel: ResourceChannel;
  resource: number;
  tags: number[];
  // "" = status unknown (channel without status integration / not re-cached)
  status: MarketingEntityRunningStatus | "" | null;
  rawStatus?: string;
  statusSyncedAt?: string | null;
}

export interface MarketingHierarchyEntityResponse<
  E extends MarketingHierarchyEntity,
> {
  // null on page 2+; MarketingPagination skips COUNT(*) there
  count: number | null;
  next: string | null;
  previous: string | null;
  results: E[];
  pageTotals: AggregatedMarketingMetrics;
  // null on page 2+ for keyword/search-term endpoints (skipped with the count)
  overallTotals: AggregatedMarketingMetrics | null;
}

export interface Account extends MarketingHierarchyEntityBase {
  hierarchyLevel: MarketingHierarchyLevel.ACCOUNT;
  hasCampaignBudget: boolean;
  hasAdGroupBudget: boolean;
}

export type AccountWithoutMetrics = Omit<
  Account,
  keyof AggregatedMarketingMetrics
>;

export enum BudgetType {
  DAILY = "DAILY",
  LIFETIME = "LIFETIME",
}

export enum BudgetLevel {
  AD_GROUP = "AD_GROUP",
  CAMPAIGN = "CAMPAIGN",
}

export interface Budget {
  amount: number;
  type: BudgetType;
  isShared: boolean;
  shareCount: number;
  name: string;
  currency: string;
}

export interface BiddingStrategy {
  type: string;
  status: string;
  name: string;
  valueLabel: string;
  valueText: string;
  valueLowerLimit: number | null;
  valueUpperLimit: number | null;
  valueExact: string | number | null;
  valueLowerLimitUnitType: MetricFormat;
  valueUpperLimitUnitType: MetricFormat;
  valueExactUnitType: MetricFormat;
}

export interface Campaign extends MarketingHierarchyEntityBase {
  hierarchyLevel: MarketingHierarchyLevel.CAMPAIGN;
  type: string;
  start: string | null;
  end: string | null;
  budget: Budget | null;
  hasAdGroupBudget: boolean;
  biddingStrategy: BiddingStrategy | null;
  isHighlighted: boolean;
}

export type CampaignWithoutMetrics = Omit<
  Campaign,
  keyof AggregatedMarketingMetrics
>;

export interface AdGroup extends MarketingHierarchyEntityBase {
  hierarchyLevel: MarketingHierarchyLevel.ADGROUP;
  campaignType: string;
  campaign: number;
  // Own status doesn't reflect a campaign pause on every channel (Google).
  campaignStatus?: MarketingEntityRunningStatus | "" | null;
  start: string | null;
  end: string | null;
  budget: Budget | null;
  hasCampaignBudget: boolean;
  isHighlighted: boolean;
}

export type AdGroupWithoutMetrics = Omit<
  AdGroup,
  keyof AggregatedMarketingMetrics
>;

export interface AdGroupKeyword extends MarketingHierarchyEntityBase {
  hierarchyLevel: MarketingHierarchyLevel.KEYWORD;
  matchType: string;
  keywordId: number;
  qualityScore?: number | null;
}

export type AdGroupKeywordWithoutMetrics = Omit<
  AdGroupKeyword,
  keyof AggregatedMarketingMetrics
>;

export interface AdGroupSearchTerm extends MarketingHierarchyEntityBase {
  hierarchyLevel: MarketingHierarchyLevel.SEARCH_TERM;
  queryId: number;
  adGroupKeywordId: number | null;
}

export type AdGroupSearchTermWithoutMetrics = Omit<
  AdGroupSearchTerm,
  keyof AggregatedMarketingMetrics
>;

export type MarketingHierarchyEntity =
  | Account
  | Campaign
  | AdGroup
  | AdGroupKeyword
  | AdGroupSearchTerm;

function getAdGroup({
  queryKey: [, { pk }],
}: QueryFunctionContext<{
  pk: number;
}>): Promise<AdGroupWithoutMetrics> {
  return api
    .get(`/resources/marketing/ad_groups/${pk}/`)
    .then((response) => response.data);
}

export function useAdGroupQuery({
  pk,
  enabled,
}: {
  pk: Ref<number | undefined>;
  enabled?: Ref<boolean>;
}) {
  const queryKey = ["adGroup", { pk: pk as Ref<number> }] as const;
  const queryClient = useQueryClient();
  return reactive(
    useQuery({
      queryKey,
      queryFn: getAdGroup,
      initialData: () => {
        return queryClient
          .getQueriesData<
            MarketingHierarchyEntityResponse<AdGroup> | undefined
          >({ queryKey: ["adGroups"] })
          .flatMap(([_, adGroupsResponse]) => adGroupsResponse?.results ?? [])
          .find((adGroup) => adGroup.pk === pk.value);
      },
      enabled: computed(
        () => pk.value !== undefined && (enabled?.value ?? true),
      ),
      staleTime: MARKETING_STALE_TIME,
      gcTime: MARKETING_GC_TIME,
    }),
  );
}

export type AdGroupOrdering =
  | "name"
  | "start"
  | "end"
  | "budget__amount"
  | MarketingMetric;

type AdGroupsParams = {
  page?: number;
  serving?: boolean;
  ordering: AscendingOrDescending<AdGroupOrdering>[];
  search: string;
  pageSize: number;
  includeTotals?: boolean;
  campaign: number | undefined;
  filterCriteria?: MetricFilterSet[];
  highlightedAdGroups: number[] | undefined;
  highlightedCampaigns: number[] | undefined;
  highlightedResources: number[] | undefined;
} & TemporalDataFilter &
  ResourcesDatafilter &
  AdNetworkFilter &
  ConversionFilter &
  TagsDataFilter &
  PKFilter;

function listAdGroups({
  queryKey: [, params],
  ...context
}:
  | QueryFunctionContext<AdGroupsParams, number | undefined>
  | QueryFunctionContext<AdGroupsParams>): Promise<
  MarketingHierarchyEntityResponse<AdGroup>
> {
  const filterCriteria = filterCriteriaFormatter(params.filterCriteria);

  return api
    .get("/resources/marketing/ad_groups/", {
      params: {
        page: "pageParam" in context ? context.pageParam : params.page,
        ...params,
        ...filterCriteria,
      },
    })
    .then((response) => response.data);
}

export function useAdGroupsQuery({
  page,
  ordering,
  search,
  pageSize,
  campaign,
  pks,
  enabled,
  filterCriteria,
  selectedResourceIds,
  conversionGroups,
  conversions,
  includedTags,
  excludedTags,
  includedTagsLogic,
  temporalFilter,
  useGlobalFilters = true,
  highlightedAdGroups,
  highlightedCampaigns,
  highlightedResources,
  metrics,
  serving,
}: {
  page: Ref<number>;
  ordering: Ref<AscendingOrDescending<AdGroupOrdering>[]>;
  search: Ref<string>;
  pageSize: Ref<number>;
  campaign: Ref<number | undefined>;
  pks?: Ref<number[]>;
  enabled?: Ref<boolean>;
  filterCriteria?: Ref<MetricFilterSet[]>;
  selectedResourceIds?: Ref<number[]>;
  conversionGroups?: Ref<number[]>;
  conversions?: Ref<number[]>;
  includedTags?: Ref<number[]>;
  excludedTags?: Ref<number[]>;
  includedTagsLogic?: Ref<"AND" | "OR">;
  temporalFilter?: Ref<DateRangeSelection>;
  useGlobalFilters?: boolean;
  highlightedAdGroups?: Ref<number[]>;
  highlightedCampaigns?: Ref<number[]>;
  highlightedResources?: Ref<number[]>;
  metrics?: Ref<string[]>;
  serving?: Ref<boolean | undefined>;
}) {
  const queryKey = [
    "adGroups",
    {
      page,
      ordering,
      search,
      pageSize,
      campaign,
      pks,
      filterCriteria,
      highlightedAdGroups,
      highlightedCampaigns,
      highlightedResources,
      serving,
      ...useDebouncedDataFilters(["subcategory"], useGlobalFilters),
      ...useDebouncedCalculatedResources(selectedResourceIds, useGlobalFilters),
      ...useDebouncedDetachedTagFilter(
        useGlobalFilters,
        includedTags,
        excludedTags,
        includedTagsLogic,
      ),
      ...useDetachedTemporalFilter(temporalFilter, useGlobalFilters),
      ...usePublicDashboardWidgetQueryFilters(),
      ...useDebouncedConversionGroups(conversionGroups, useGlobalFilters),
      ...useConversions(conversions, useGlobalFilters),
      ...useMetricFilter(metrics, useGlobalFilters),
    },
  ] as const;
  useResetPage(page, queryKey, [
    "highlightedAdGroups",
    "highlightedCampaigns",
    "highlightedResources",
  ]);
  return reactive(
    useQuery({
      queryKey,
      queryFn: listAdGroups,
      enabled: computed(() => pageSize.value > 0 && (enabled?.value ?? true)),
      placeholderData: (previousData) =>
        page.value > 1 ? previousData : undefined,
      staleTime: MARKETING_STALE_TIME,
    }),
  );
}

export const useAdGroupsQueryWithMetrics = {
  query: useAdGroupsQuery,
  metrics: [...BASE_MARKETING_METRICS, ...MARKETING_NONAGR_METRICS],
};

export function useAdGroupsInfiniteQuery({
  ordering,
  search,
  pageSize,
  includeTotals,
  campaign,
  enabled,
  selectedResourceIds,
  includedTags,
  excludedTags,
  includedTagsLogic,
  temporalFilter,
  useGlobalFilters = true,
  highlightedAdGroups,
  highlightedCampaigns,
  highlightedResources,
}: {
  ordering: Ref<AscendingOrDescending<AdGroupOrdering>[]>;
  search: Ref<string>;
  pageSize: Ref<number>;
  includeTotals: Ref<boolean>;
  campaign: Ref<number | undefined>;
  enabled?: Ref<boolean>;
  selectedResourceIds?: Ref<number[]>;
  includedTags?: Ref<number[]>;
  excludedTags?: Ref<number[]>;
  includedTagsLogic?: Ref<"AND" | "OR">;
  temporalFilter?: Ref<DateRangeSelection>;
  useGlobalFilters?: boolean;
  highlightedAdGroups?: Ref<number[]>;
  highlightedCampaigns?: Ref<number[]>;
  highlightedResources?: Ref<number[]>;
}) {
  const queryKey = [
    "infiniteAdGroups",
    {
      ordering,
      search,
      pageSize,
      includeTotals,
      campaign,
      highlightedAdGroups,
      highlightedCampaigns,
      highlightedResources,
      ...useDebouncedDataFilters(
        ["subcategory", "conversionGroup", "conversion"],
        useGlobalFilters,
      ),
      ...useDebouncedCalculatedResources(selectedResourceIds, useGlobalFilters),
      ...useDebouncedDetachedTagFilter(
        useGlobalFilters,
        includedTags,
        excludedTags,
        includedTagsLogic,
      ),
      ...useDetachedTemporalFilter(temporalFilter, useGlobalFilters),
    },
  ] as const;
  return reactive(
    useInfiniteQuery({
      queryKey,
      queryFn: listAdGroups,
      enabled,
      placeholderData: keepPreviousData,
      initialPageParam: undefined,
      getNextPageParam: (lastPage, pages) =>
        lastPage.next ? pages.length + 1 : undefined,
    }),
  );
}

function getCampaign({
  queryKey: [, { pk }],
}: QueryFunctionContext<{
  pk: number;
}>): Promise<CampaignWithoutMetrics> {
  return api
    .get(`/resources/marketing/campaigns/${pk}/`)
    .then((response) => response.data);
}

export function useCampaignQuery({
  pk,
  enabled,
}: {
  pk: Ref<number | undefined>;
  enabled?: Ref<boolean>;
}) {
  const queryKey = ["campaign", { pk: pk as Ref<number> }] as const;
  const queryClient = useQueryClient();
  return reactive(
    useQuery({
      queryKey,
      queryFn: getCampaign,
      initialData: () => {
        return queryClient
          .getQueriesData<
            MarketingHierarchyEntityResponse<Campaign> | undefined
          >({ queryKey: ["campaigns"] })
          .flatMap(([_, campaignsResponse]) => campaignsResponse?.results ?? [])
          .find((campaign) => campaign.pk === pk.value);
      },
      enabled: computed(
        () => pk.value !== undefined && (enabled?.value ?? true),
      ),
      staleTime: MARKETING_STALE_TIME,
      gcTime: MARKETING_GC_TIME,
    }),
  );
}
export const useCampaignQueryWithMetrics = {
  query: useCampaignQuery,
  metrics: BASE_MARKETING_METRICS,
};

export type CampaignOrdering =
  | "name"
  | "start"
  | "end"
  | "budget__amount"
  | "bidding_strategy__type"
  | MarketingMetric;

type CampaignsParams = {
  page?: number;
  ordering: AscendingOrDescending<CampaignOrdering>[];
  search: string;
  pageSize: number;
  includeTotals?: boolean;
  serving?: boolean;
  filterCriteria?: MetricFilterSet[];
  highlightedCampaigns: number[] | undefined;
  highlightedResources: number[] | undefined;
} & TemporalDataFilter &
  ResourcesDatafilter &
  AdNetworkFilter &
  ConversionFilter &
  TagsDataFilter &
  PKFilter;

function listCampaigns({
  queryKey: [, params],
  ...context
}:
  | QueryFunctionContext<CampaignsParams, number | undefined>
  | QueryFunctionContext<CampaignsParams>): Promise<
  MarketingHierarchyEntityResponse<Campaign>
> {
  const filterCriteria = filterCriteriaFormatter(params.filterCriteria);

  return api
    .get("/resources/marketing/campaigns/", {
      params: {
        page: "pageParam" in context ? context.pageParam : params.page,
        ...params,
        ...filterCriteria,
      },
    })
    .then((response) => response.data);
}

export function useCampaignsQuery({
  page,
  ordering,
  search,
  pageSize,
  pks,
  enabled,
  filterCriteria,
  selectedResourceIds,
  conversionGroups,
  conversions,
  includedTags,
  excludedTags,
  includedTagsLogic,
  temporalFilter,
  useGlobalFilters = true,
  highlightedCampaigns,
  highlightedResources,
  metrics,
  serving,
}: {
  page: Ref<number>;
  ordering: Ref<AscendingOrDescending<CampaignOrdering>[]>;
  search: Ref<string>;
  pageSize: Ref<number>;
  pks?: Ref<number[]>;
  enabled?: Ref<boolean>;
  filterCriteria?: Ref<MetricFilterSet[]>;
  selectedResourceIds?: Ref<number[]>;
  conversionGroups?: Ref<number[]>;
  conversions?: Ref<number[]>;
  includedTags?: Ref<number[]>;
  excludedTags?: Ref<number[]>;
  includedTagsLogic?: Ref<"AND" | "OR">;
  temporalFilter?: Ref<DateRangeSelection>;
  useGlobalFilters?: boolean;
  highlightedCampaigns?: Ref<number[]>;
  highlightedResources?: Ref<number[]>;
  metrics?: Ref<string[]>;
  serving?: Ref<boolean | undefined>;
}) {
  const queryKey = [
    "campaigns",
    {
      page,
      ordering,
      search,
      pageSize,
      pks,
      filterCriteria,
      highlightedCampaigns,
      highlightedResources,
      serving,
      ...useDebouncedDataFilters(["subcategory"], useGlobalFilters),
      ...useDebouncedCalculatedResources(selectedResourceIds, useGlobalFilters),
      ...useDebouncedDetachedTagFilter(
        useGlobalFilters,
        includedTags,
        excludedTags,
        includedTagsLogic,
      ),
      ...useDetachedTemporalFilter(temporalFilter, useGlobalFilters),
      ...usePublicDashboardWidgetQueryFilters(),
      ...useDebouncedConversionGroups(conversionGroups, useGlobalFilters),
      ...useConversions(conversions, useGlobalFilters),
      ...useMetricFilter(metrics, useGlobalFilters),
    },
  ] as const;
  useResetPage(page, queryKey, [
    "highlightedCampaigns",
    "highlightedResources",
  ]);
  return reactive(
    useQuery({
      queryKey,
      queryFn: listCampaigns,
      enabled: computed(() => pageSize.value > 0 && (enabled?.value ?? true)),
      placeholderData: (previousData) =>
        page.value > 1 ? previousData : undefined,
      staleTime: MARKETING_STALE_TIME,
    }),
  );
}
export const useCampaignsQueryWithMetrics = {
  query: useCampaignsQuery,
  metrics: [...BASE_MARKETING_METRICS, ...MARKETING_NONAGR_METRICS],
};

export function useCampaignsInfiniteQuery({
  ordering,
  search,
  pageSize,
  includeTotals,
  enabled,
  selectedResourceIds,
  includedTags,
  excludedTags,
  includedTagsLogic,
  temporalFilter,
  useGlobalFilters = true,
  highlightedCampaigns,
  highlightedResources,
}: {
  ordering: Ref<AscendingOrDescending<CampaignOrdering>[]>;
  search: Ref<string>;
  pageSize: Ref<number>;
  includeTotals: Ref<boolean>;
  enabled?: Ref<boolean>;
  selectedResourceIds?: Ref<number[]>;
  includedTags?: Ref<number[]>;
  excludedTags?: Ref<number[]>;
  includedTagsLogic?: Ref<"AND" | "OR">;
  temporalFilter?: Ref<DateRangeSelection>;
  useGlobalFilters?: boolean;
  highlightedCampaigns?: Ref<number[]>;
  highlightedResources?: Ref<number[]>;
}) {
  const queryKey = [
    "infiniteCampaigns",
    {
      ordering,
      search,
      pageSize,
      includeTotals,
      highlightedCampaigns,
      highlightedResources,
      ...useDebouncedDataFilters(
        ["subcategory", "conversionGroup", "conversion"],
        useGlobalFilters,
      ),
      ...useDebouncedCalculatedResources(selectedResourceIds, useGlobalFilters),
      ...useDebouncedDetachedTagFilter(
        useGlobalFilters,
        includedTags,
        excludedTags,
        includedTagsLogic,
      ),
      ...useDetachedTemporalFilter(temporalFilter, useGlobalFilters),
    },
  ] as const;
  return reactive(
    useInfiniteQuery({
      queryKey,
      queryFn: listCampaigns,
      enabled,
      placeholderData: keepPreviousData,
      initialPageParam: undefined,
      getNextPageParam: (lastPage, pages) =>
        lastPage.next ? pages.length + 1 : undefined,
    }),
  );
}

function getAccount({
  queryKey: [, { pk }],
}: QueryFunctionContext<{
  pk: number;
}>): Promise<AdGroupWithoutMetrics> {
  return api
    .get(`/resources/marketing/accounts/${pk}/`)
    .then((response) => response.data);
}

export function useAccountQuery({
  pk,
  enabled,
}: {
  pk: Ref<number | undefined>;
  enabled?: Ref<boolean>;
}) {
  const queryKey = ["account", { pk: pk as Ref<number> }] as const;
  const queryClient = useQueryClient();
  return reactive(
    useQuery({
      queryKey,
      queryFn: getAccount,
      initialData: () => {
        return queryClient
          .getQueriesData<
            MarketingHierarchyEntityResponse<AdGroup> | undefined
          >({ queryKey: ["accounts"] })
          .flatMap(([_, accountsResponse]) => accountsResponse?.results ?? [])
          .find((account) => account.pk === pk.value);
      },
      enabled: computed(
        () => pk.value !== undefined && (enabled?.value ?? true),
      ),
      staleTime: MARKETING_STALE_TIME,
      gcTime: MARKETING_GC_TIME,
    }),
  );
}

export type AccountOrdering = "name" | MarketingMetric;

function listAccounts({
  queryKey: [, params],
}: QueryFunctionContext<
  {
    page: number;
    ordering: AscendingOrDescending<AccountOrdering>[];
    search: string;
    pageSize: number;
    includeTotals?: boolean;
    filterCriteria?: MetricFilterSet[];
  } & TemporalDataFilter &
    ResourcesDatafilter &
    AdNetworkFilter &
    ConversionFilter &
    TagsDataFilter &
    PKFilter
>): Promise<MarketingHierarchyEntityResponse<Account>> {
  const filterCriteria = filterCriteriaFormatter(params.filterCriteria);

  return api
    .get("/resources/marketing/accounts/", {
      params: { ...params, ...filterCriteria },
    })
    .then((response) => response.data);
}

export function useAccountsQuery({
  page,
  ordering,
  search,
  pageSize,
  pks,
  enabled,
  filterCriteria,
  conversionGroups,
  conversions,
  temporalFilter,
  useGlobalFilters = true,
  metrics,
}: {
  page: Ref<number>;
  ordering: Ref<AscendingOrDescending<AccountOrdering>[]>;
  search: Ref<string>;
  pageSize: Ref<number>;
  pks?: Ref<number[]>;
  enabled?: Ref<boolean>;
  filterCriteria?: Ref<MetricFilterSet[]>;
  conversionGroups?: Ref<number[]>;
  conversions?: Ref<number[]>;
  temporalFilter?: Ref<DateRangeSelection>;
  useGlobalFilters?: boolean;
  metrics?: Ref<string[]>;
}) {
  const normalizedPks = computed(() =>
    ((pks?.value as (string | number)[]) ?? []).map((id) =>
      typeof id === "string" ? Number(id.split("-")[0]) : id,
    ),
  );
  const queryKey = [
    "accounts",
    {
      page,
      ordering,
      search,
      pageSize,
      pks: normalizedPks,
      filterCriteria,

      ...useDebouncedDataFilters(
        ["includedResources", "subcategory", "includedTags", "excludedTags"],
        useGlobalFilters,
      ),
      ...useDetachedTemporalFilter(temporalFilter, useGlobalFilters),
      ...usePublicDashboardWidgetQueryFilters(),
      ...useDebouncedConversionGroups(conversionGroups, useGlobalFilters),
      ...useConversions(conversions, useGlobalFilters),
      ...useMetricFilter(metrics, useGlobalFilters),
    },
  ] as const;
  useResetPage(page, queryKey);
  return reactive(
    useQuery({
      queryKey,
      queryFn: listAccounts,
      enabled: computed(() => pageSize.value > 0 && (enabled?.value ?? true)),
      placeholderData: (previousData) =>
        page.value > 1 ? previousData : undefined,
      staleTime: MARKETING_STALE_TIME,
    }),
  );
}
export const useAccountsQueryWithMetrics = {
  query: useAccountsQuery,
  metrics: [...BASE_MARKETING_METRICS, ...MARKETING_NONAGR_METRICS],
};

function getAdGroupKeyword({
  queryKey: [, { pk }],
}: QueryFunctionContext<{
  pk: number;
}>): Promise<AdGroupKeyword> {
  return api
    .get(`/resources/marketing/keywords/${pk}/`)
    .then((response) => response.data);
}

export function useAdGroupKeywordQuery({
  pk,
  enabled,
}: {
  pk: Ref<number | undefined>;
  enabled?: Ref<boolean>;
}) {
  const queryKey = ["adGroupKeyword", { pk: pk as Ref<number> }] as const;
  const queryClient = useQueryClient();
  return reactive(
    useQuery({
      queryKey,
      queryFn: getAdGroupKeyword,
      initialData: () => {
        return queryClient
          .getQueriesData<
            MarketingHierarchyEntityResponse<AdGroupKeyword> | undefined
          >({ queryKey: ["adGroupKeywords"] })
          .flatMap(([_, keywordsResponse]) => keywordsResponse?.results ?? [])
          .find((keyword) => keyword.pk === pk.value);
      },
      enabled: computed(
        () => pk.value !== undefined && (enabled?.value ?? true),
      ),
      staleTime: MARKETING_STALE_TIME,
      gcTime: MARKETING_GC_TIME,
    }),
  );
}

export type AdGroupKeywordOrdering = "name" | "quality_score" | MarketingMetric;

type AdGroupKeywordsParams = {
  page?: number;
  ordering: AscendingOrDescending<AdGroupKeywordOrdering>[];
  search: string;
  pageSize: number;
  includeTotals?: boolean;
  adGroup: number | undefined;
  campaign: number | undefined;
  filterCriteria?: MetricFilterSet[];
  metrics?: string[];
} & TemporalDataFilter &
  ResourcesDatafilter &
  ConversionFilter &
  TagsDataFilter &
  PKFilter;

function listAdGroupKeywords({
  queryKey: [, params],
  ...context
}:
  | QueryFunctionContext<AdGroupKeywordsParams, number | undefined>
  | QueryFunctionContext<AdGroupKeywordsParams>): Promise<
  MarketingHierarchyEntityResponse<AdGroupKeyword>
> {
  const filterCriteria = filterCriteriaFormatter(params.filterCriteria);
  const { metrics, ...remaningParams } = params;
  return api
    .get("/resources/marketing/keywords/", {
      params: {
        page: "pageParam" in context ? context.pageParam : remaningParams.page,
        ...remaningParams,
        ...filterCriteria,
        ...(metrics ? { fields: metrics } : {}),
      },
      timeout: 5 * 60 * 1000, // 5 minutes
    })
    .then((response) => response.data);
}

export function useAdGroupKeywordsQuery({
  page,
  ordering,
  search,
  pageSize,
  adGroup,
  campaign,
  pks,
  enabled,
  filterCriteria,
  selectedResourceIds,
  conversionGroups,
  conversions,
  includedTags,
  excludedTags,
  includedTagsLogic,
  temporalFilter,
  useGlobalFilters = true,
  metrics,
}: {
  page: Ref<number>;
  ordering: Ref<AscendingOrDescending<AdGroupKeywordOrdering>[]>;
  search: Ref<string>;
  pageSize: Ref<number>;
  adGroup: Ref<number | undefined>;
  campaign: Ref<number | undefined>;
  pks?: Ref<number[]>;
  enabled?: Ref<boolean>;
  filterCriteria?: Ref<MetricFilterSet[]>;
  selectedResourceIds?: Ref<number[]>;
  conversionGroups?: Ref<number[]>;
  conversions?: Ref<number[]>;
  includedTags?: Ref<number[]>;
  excludedTags?: Ref<number[]>;
  includedTagsLogic?: Ref<"AND" | "OR">;
  temporalFilter?: Ref<DateRangeSelection>;
  useGlobalFilters?: boolean;
  metrics?: Ref<string[]>;
}) {
  const queryKey = [
    "adGroupKeywords",
    {
      page,
      ordering,
      search,
      pageSize,
      adGroup,
      campaign,
      pks,
      filterCriteria,
      metrics,
      ...useDetachedTemporalFilter(temporalFilter, useGlobalFilters),
      ...useDebouncedCalculatedResources(selectedResourceIds, useGlobalFilters),
      ...useDebouncedDetachedTagFilter(
        useGlobalFilters,
        includedTags,
        excludedTags,
        includedTagsLogic,
      ),
      ...usePublicDashboardWidgetQueryFilters(),
      ...useDebouncedConversionGroups(conversionGroups, useGlobalFilters),
      ...useConversions(conversions, useGlobalFilters),
    },
  ] as const;
  useResetPage(page, queryKey);
  return reactive(
    useQuery({
      queryKey,
      queryFn: listAdGroupKeywords,
      enabled: computed(() => pageSize.value > 0 && (enabled?.value ?? true)),
      placeholderData: (previousData) =>
        page.value > 1 ? previousData : undefined,
      staleTime: MARKETING_STALE_TIME,
    }),
  );
}

export const useAdGroupKeywordQueryWithMetrics = {
  query: useAdGroupKeywordsQuery,
  metrics: [
    ...BASE_MARKETING_METRICS,
    ...MARKETING_NONAGR_METRICS,
    "qualityScore",
  ] as const,
};

export function useAdGroupKeywordsInfiniteQuery({
  ordering,
  search,
  pageSize,
  includeTotals,
  adGroup,
  campaign,
  enabled,
  selectedResourceIds,
  includedTags,
  excludedTags,
  includedTagsLogic,
  temporalFilter,
  useGlobalFilters = true,
}: {
  ordering: Ref<AscendingOrDescending<AdGroupKeywordOrdering>[]>;
  search: Ref<string>;
  pageSize: Ref<number>;
  includeTotals: Ref<boolean>;
  adGroup: Ref<number | undefined>;
  campaign: Ref<number | undefined>;
  enabled?: Ref<boolean>;
  selectedResourceIds?: Ref<number[]>;
  includedTags?: Ref<number[]>;
  excludedTags?: Ref<number[]>;
  includedTagsLogic?: Ref<"AND" | "OR">;
  temporalFilter?: Ref<DateRangeSelection>;
  useGlobalFilters?: boolean;
}) {
  const queryKey = [
    "infiniteAdGroupKeywords",
    {
      ordering,
      search,
      pageSize,
      includeTotals,
      adGroup,
      campaign,
      ...useDebouncedDataFilters(
        ["conversionGroup", "conversion"],
        useGlobalFilters,
      ),
      ...useDebouncedCalculatedResources(selectedResourceIds, useGlobalFilters),
      ...useDebouncedDetachedTagFilter(
        useGlobalFilters,
        includedTags,
        excludedTags,
        includedTagsLogic,
      ),
      ...useDetachedTemporalFilter(temporalFilter, useGlobalFilters),
    },
  ] as const;

  return reactive(
    useInfiniteQuery({
      queryKey,
      queryFn: listAdGroupKeywords,
      enabled,
      placeholderData: keepPreviousData,
      initialPageParam: undefined,
      getNextPageParam: (lastPage, pages) =>
        lastPage.next ? pages.length + 1 : undefined,
    }),
  );
}

export type AdGroupSearchTermOrdering = "name" | MarketingMetric;

type AdGroupSearchTermParams = {
  page?: number;
  ordering: AscendingOrDescending<AdGroupSearchTermOrdering>[];
  search: string;
  pageSize: number;
  includeTotals?: boolean;
  adGroupKeyword: number | undefined;
  adGroup: number | undefined;
  campaign: number | undefined;
  filterCriteria?: MetricFilterSet[];
  metrics?: string[];
} & TemporalDataFilter &
  ResourcesDatafilter &
  ConversionFilter &
  TagsDataFilter &
  PKFilter;

function listAdGroupSearchTerms({
  queryKey: [, params],
}:
  | QueryFunctionContext<AdGroupSearchTermParams, number | undefined>
  | QueryFunctionContext<AdGroupSearchTermParams>): Promise<
  MarketingHierarchyEntityResponse<AdGroupSearchTerm>
> {
  const filterCriteria = filterCriteriaFormatter(params.filterCriteria);
  const { metrics, ...remainingParams } = params;
  return api
    .get("/resources/marketing/queries/", {
      params: {
        ...remainingParams,
        ...filterCriteria,
        ...(metrics ? { fields: metrics } : {}),
      },
    })
    .then((response) => response.data);
}

export function useAdGroupSearchTermsQuery({
  page,
  ordering,
  search,
  pageSize,
  adGroupKeyword,
  adGroup,
  campaign,
  pks,
  enabled,
  filterCriteria,
  selectedResourceIds,
  conversionGroups,
  conversions,
  includedTags,
  excludedTags,
  includedTagsLogic,
  temporalFilter,
  useGlobalFilters = true,
  metrics,
}: {
  page: Ref<number>;
  ordering: Ref<AscendingOrDescending<AdGroupSearchTermOrdering>[]>;
  search: Ref<string>;
  pageSize: Ref<number>;
  adGroupKeyword: Ref<number | undefined>;
  adGroup: Ref<number | undefined>;
  campaign: Ref<number | undefined>;
  pks?: Ref<number[]>;
  enabled?: Ref<boolean>;
  filterCriteria?: Ref<MetricFilterSet[]>;
  selectedResourceIds?: Ref<number[]>;
  conversionGroups?: Ref<number[]>;
  conversions?: Ref<number[]>;
  includedTags?: Ref<number[]>;
  excludedTags?: Ref<number[]>;
  includedTagsLogic?: Ref<"AND" | "OR">;
  temporalFilter?: Ref<DateRangeSelection>;
  useGlobalFilters?: boolean;
  metrics?: Ref<string[]>;
}) {
  const queryKey = [
    "adGroupSearchTerms",
    {
      page,
      ordering,
      search,
      pageSize,
      adGroupKeyword,
      adGroup,
      campaign,
      pks,
      filterCriteria,
      metrics,
      ...useDetachedTemporalFilter(temporalFilter, useGlobalFilters),
      ...useDebouncedCalculatedResources(selectedResourceIds, useGlobalFilters),
      ...useDebouncedDetachedTagFilter(
        useGlobalFilters,
        includedTags,
        excludedTags,
        includedTagsLogic,
      ),
      ...usePublicDashboardWidgetQueryFilters(),
      ...useDebouncedConversionGroups(conversionGroups, useGlobalFilters),
      ...useConversions(conversions, useGlobalFilters),
    },
  ] as const;
  useResetPage(page, queryKey);
  return reactive(
    useQuery({
      queryKey,
      queryFn: listAdGroupSearchTerms,
      enabled: computed(() => pageSize.value > 0 && (enabled?.value ?? true)),
      placeholderData: (previousData) =>
        page.value > 1 ? previousData : undefined,
      staleTime: MARKETING_STALE_TIME,
    }),
  );
}

export const useAdGroupSearchTermsQueryWithMetrics = {
  query: useAdGroupSearchTermsQuery,
  metrics: BASE_MARKETING_METRICS,
};

export function useAdGroupSearchTermsInfiniteQuery({
  ordering,
  search,
  pageSize,
  includeTotals,
  adGroupKeyword,
  adGroup,
  campaign,
  pks,
  enabled,
  filterCriteria,
  selectedResourceIds,
  includedTags,
  excludedTags,
  includedTagsLogic,
  temporalFilter,
  useGlobalFilters = true,
}: {
  ordering: Ref<AscendingOrDescending<AdGroupSearchTermOrdering>[]>;
  search: Ref<string>;
  pageSize: Ref<number>;
  includeTotals: Ref<boolean>;
  adGroupKeyword: Ref<number | undefined>;
  adGroup: Ref<number | undefined>;
  campaign: Ref<number | undefined>;
  pks?: Ref<number[]>;
  enabled?: Ref<boolean>;
  filterCriteria?: Ref<MetricFilterSet[]>;
  selectedResourceIds?: Ref<number[]>;
  includedTags?: Ref<number[]>;
  excludedTags?: Ref<number[]>;
  includedTagsLogic?: Ref<"AND" | "OR">;
  temporalFilter?: Ref<DateRangeSelection>;
  useGlobalFilters?: boolean;
}) {
  const queryKey = [
    "infiniteAdGroupSearchTerms",
    {
      ordering,
      search,
      pageSize,
      includeTotals,
      adGroupKeyword,
      adGroup,
      campaign,
      pks,
      filterCriteria,
      ...useDebouncedDataFilters(
        ["conversionGroup", "conversion"],
        useGlobalFilters,
      ),
      ...useDebouncedCalculatedResources(selectedResourceIds, useGlobalFilters),
      ...useDebouncedDetachedTagFilter(
        useGlobalFilters,
        includedTags,
        excludedTags,
        includedTagsLogic,
      ),
      ...useDetachedTemporalFilter(temporalFilter, useGlobalFilters),
    },
  ] as const;
  return reactive(
    useInfiniteQuery({
      queryKey,
      queryFn: listAdGroupSearchTerms,
      enabled,
      placeholderData: keepPreviousData,
      initialPageParam: undefined,
      getNextPageParam: (lastPage, pages) =>
        lastPage.next ? pages.length + 1 : undefined,
    }),
  );
}

// Deliberately without "status", which `MarketingTableMetricKey` does carry:
// status is a displayable column but not an orderable one (the badge is
// parent-aware, and the backend omits it from `ordering_fields`).
export type MarketingOrdering =
  | MarketingMetric
  | "name"
  | "start"
  | "end"
  | "budget"
  | "biddingStrategy"
  | "qualityScore";

export function useMarketingTableDataQuery(
  currentRouteHierarchyLevel: Ref<MarketingHierarchyLevel | undefined>,
  selectedTableHierarchyLevel: Ref<MarketingHierarchyLevel>,
  entityId: Ref<number | undefined>,
  pageSize: Ref<number>,
  page: Ref<number>,
  search: Ref<string>,
  filterCriteria: Ref<MetricFilterSet[]>,
  sortedByColumn: Ref<MarketingOrdering>,
  sortedDirection: Ref<TableSortDirection>,
  timeUnit: Ref<TimeUnit>,
  isEnabled: Ref<boolean>,
  pks?: Ref<number[]>,
  highlightedAdGroups?: Ref<number[]>,
  highlightedCampaigns?: Ref<number[]>,
  highlightedResources?: Ref<number[]>,
  selectedmetrics?: Ref<string[]>,
  serving?: Ref<boolean | undefined>,
) {
  // Use debounced filters to prevent excessive API calls
  const { debouncedIncludedResources } = useDebouncedFilters();

  const selectedmetricsWithSorted = computed(() => {
    let attribute: string = sortedByColumn.value;
    if (sortedByColumn.value === "budget") {
      attribute = "budget__amount";
    }
    if (sortedByColumn.value === "biddingStrategy") {
      attribute = "bidding_strategy__type";
    }
    if (sortedByColumn.value === "qualityScore") {
      attribute = "quality_score";
    }

    if (typeof selectedmetrics === "undefined") {
      return [];
    }
    // Status support needs no field mapping here: the main table query
    // passes no `fields` param at all, so the serializer returns the full
    // field set including status/rawStatus/statusSyncedAt/parent statuses.
    return [...selectedmetrics.value, attribute];
  });
  const ordering = computed(() => {
    let attribute: string = sortedByColumn.value;
    if (sortedByColumn.value === "budget") {
      attribute = "budget__amount";
    }
    if (sortedByColumn.value === "biddingStrategy") {
      attribute = "bidding_strategy__type";
    }
    if (sortedByColumn.value === "qualityScore") {
      attribute = "quality_score";
    }

    const ordering = [orderingKey(attribute, sortedDirection.value)];
    if (sortedByColumn.value === "conversions") {
      ordering.push(orderingKey("impressions", TableSortDirection.DESC));
    }
    return ordering;
  });
  const campaign = computed(() =>
    currentRouteHierarchyLevel.value === "campaign"
      ? entityId.value
      : undefined,
  );
  const adGroup = computed(() =>
    currentRouteHierarchyLevel.value === "adGroup" ? entityId.value : undefined,
  );
  const selectedResourceIds = computed(() => {
    if (currentRouteHierarchyLevel.value === MarketingHierarchyLevel.ACCOUNT) {
      return [entityId.value as number];
    } else {
      // Use debounced filter instead of direct store access
      return debouncedIncludedResources.value;
    }
  });

  const accounts = useAccountsQuery({
    page,
    ordering: ordering as Ref<AscendingOrDescending<AccountOrdering>[]>,
    search,
    pageSize,
    pks,
    filterCriteria,
    enabled: computed(
      () =>
        selectedTableHierarchyLevel.value === MarketingHierarchyLevel.ACCOUNT &&
        isEnabled.value,
    ),
    metrics: selectedmetricsWithSorted,
  });
  const campaigns = useCampaignsQuery({
    page,
    ordering: ordering as Ref<AscendingOrDescending<CampaignOrdering>[]>,
    search,
    pageSize,
    pks,
    filterCriteria,
    enabled: computed(
      () =>
        selectedTableHierarchyLevel.value ===
          MarketingHierarchyLevel.CAMPAIGN && isEnabled.value,
    ),
    selectedResourceIds,
    highlightedCampaigns,
    highlightedResources,
    metrics: selectedmetricsWithSorted,
    serving,
  });
  const adGroups = useAdGroupsQuery({
    page,
    ordering: ordering as Ref<AscendingOrDescending<AdGroupOrdering>[]>,
    search,
    pageSize,
    campaign,
    pks,
    filterCriteria,
    enabled: computed(
      () =>
        selectedTableHierarchyLevel.value === MarketingHierarchyLevel.ADGROUP &&
        isEnabled.value,
    ),
    selectedResourceIds,
    highlightedAdGroups,
    highlightedCampaigns,
    highlightedResources,
    metrics: selectedmetricsWithSorted,
    serving,
  });
  const ads = useAdsQuery({
    page,
    ordering: ordering as Ref<AscendingOrDescending<AccountOrdering>[]>,
    search,
    pageSize,
    includeTotals: ref(true),
    includeMetricsByDate: ref(false),
    adGroup,
    campaign,
    pks,
    filterCriteria,
    enabled: computed(
      () =>
        selectedTableHierarchyLevel.value === MarketingHierarchyLevel.AD &&
        isEnabled.value,
    ),
    timeUnit,
    selectedResourceIds,
    metrics: selectedmetricsWithSorted,
    serving,
  });
  const keywords = useAdGroupKeywordsQuery({
    page,
    ordering: ordering as Ref<AscendingOrDescending<AccountOrdering>[]>,
    search,
    pageSize,
    adGroup,
    campaign,
    filterCriteria,
    enabled: computed(
      () =>
        selectedTableHierarchyLevel.value === MarketingHierarchyLevel.KEYWORD &&
        isEnabled.value,
    ),
    selectedResourceIds,
    metrics: selectedmetricsWithSorted,
  });
  const searchTerms = useAdGroupSearchTermsQuery({
    page,
    ordering: ordering as Ref<AscendingOrDescending<AccountOrdering>[]>,
    search,
    pageSize,
    adGroupKeyword: computed(() =>
      currentRouteHierarchyLevel.value === "keyword"
        ? entityId.value
        : undefined,
    ),
    adGroup,
    campaign,
    filterCriteria,
    enabled: computed(
      () =>
        selectedTableHierarchyLevel.value ===
          MarketingHierarchyLevel.SEARCH_TERM && isEnabled.value,
    ),
    selectedResourceIds,
    metrics: selectedmetricsWithSorted,
  });

  return computed(() => {
    return {
      [MarketingHierarchyLevel.ACCOUNT]: accounts,
      [MarketingHierarchyLevel.CAMPAIGN]: campaigns,
      [MarketingHierarchyLevel.ADGROUP]: adGroups,
      [MarketingHierarchyLevel.AD]: ads,
      [MarketingHierarchyLevel.KEYWORD]: keywords,
      [MarketingHierarchyLevel.SEARCH_TERM]: searchTerms,
    }[selectedTableHierarchyLevel.value];
  });
}

export function useMarketingChartDataQuery(
  currentRouteHierarchyLevel: Ref<MarketingHierarchyLevel | undefined>,
  hierarchyLevel: Ref<MarketingHierarchyLevel>,
  pk: Ref<number | undefined>,
  selectedEntities: Ref<number[]>,
  timeUnit: Ref<TimeUnit>,
  isEnabled: Ref<boolean>,
  selectedmetrics?: Ref<string[]>,
) {
  const { debouncedIncludedResources } = useDebouncedFilters();
  const selectedResourceIds = computed(() => {
    if (currentRouteHierarchyLevel.value === MarketingHierarchyLevel.ACCOUNT) {
      return [pk.value as number];
    } else {
      return debouncedIncludedResources.value;
    }
  });

  return useMarketingMetricsByDateAndPlatformQuery({
    // If we're not comparing, but we're on the page for a specific entity,
    // we use this to filter the data by that entity.
    // If we're comparing we use it to filter by the compared entities instead.
    hierarchyLevel: computed(() =>
      selectedEntities.value.length === 0 && pk.value
        ? currentRouteHierarchyLevel.value
        : hierarchyLevel.value,
    ),
    tagHierarchyLevel: hierarchyLevel,
    selectedEntities: computed(() =>
      selectedEntities.value.length === 0 && pk.value
        ? [pk.value]
        : selectedEntities.value,
    ),
    selectedResourceIds,
    timeUnit,
    metrics: selectedmetrics,
    enabled: isEnabled,
  });
}

export function useMarketingGridDataQuery(
  currentRouteHierarchyLevel: Ref<MarketingHierarchyLevel | undefined>,
  hierarchyLevel: Ref<MarketingHierarchyLevel>,
  pk: Ref<number | undefined>,
  selectedEntities: Ref<number[]>,
  enabled: Ref<boolean>,
  temporalFilter?: Ref<DateRangeSelection>,
  selectedmetrics?: Ref<string[]>,
) {
  const { debouncedIncludedResources } = useDebouncedFilters();
  const selectedResourceIds = computed(() => {
    if (currentRouteHierarchyLevel.value === MarketingHierarchyLevel.ACCOUNT) {
      return [pk.value as number];
    } else {
      return debouncedIncludedResources.value;
    }
  });

  return useMarketingMetricsByComparisonQuery({
    // If we're not comparing, but we're on the page for a specific entity,
    // we use this to filter the data by that entity.
    // If we're comparing we use it to filter by the compared entities instead.
    hierarchyLevel: computed(() =>
      selectedEntities.value.length === 0 && pk.value
        ? currentRouteHierarchyLevel.value
        : hierarchyLevel.value,
    ),
    tagHierarchyLevel: hierarchyLevel,
    selectedEntities: computed(() =>
      selectedEntities.value.length === 0 && pk.value
        ? [pk.value]
        : selectedEntities.value,
    ),
    selectedResourceIds,
    // For making stat cards always take in a comparison period
    temporalFilter,
    metrics: selectedmetrics,
    enabled: enabled,
  });
}

export function useMarketingEntity(
  currentRouteHierarchyLevel: Ref<MarketingHierarchyLevel | undefined>,
  pk: Ref<number | undefined>,
) {
  const account = useAccountQuery({
    pk,
    enabled: computed(
      () =>
        currentRouteHierarchyLevel.value === MarketingHierarchyLevel.ACCOUNT,
    ),
  });
  const keyword = useAdGroupKeywordQuery({
    pk,
    enabled: computed(
      () =>
        currentRouteHierarchyLevel.value === MarketingHierarchyLevel.KEYWORD,
    ),
  });
  const adGroup = useAdGroupQuery({
    pk,
    enabled: computed(
      () =>
        currentRouteHierarchyLevel.value === MarketingHierarchyLevel.ADGROUP,
    ),
  });
  const campaign = useCampaignQuery({
    pk,
    enabled: computed(
      () =>
        currentRouteHierarchyLevel.value === MarketingHierarchyLevel.CAMPAIGN,
    ),
  });
  return computed(() => {
    if (currentRouteHierarchyLevel.value === MarketingHierarchyLevel.ACCOUNT) {
      return account;
    } else if (
      currentRouteHierarchyLevel.value === MarketingHierarchyLevel.CAMPAIGN
    ) {
      return campaign;
    } else if (
      currentRouteHierarchyLevel.value === MarketingHierarchyLevel.ADGROUP
    ) {
      return adGroup;
    } else if (
      currentRouteHierarchyLevel.value === MarketingHierarchyLevel.KEYWORD
    ) {
      return keyword;
    } else {
      return undefined;
    }
  });
}

export function useBulkEditMarketingPerformanceTagsMutation() {
  const queryClient = useQueryClient();
  return reactive(
    useMutation({
      mutationFn: bulkEditMarketingPerformanceTags,
      onSettled: (_, __, { tableHierarchyLevel }) => {
        if (tableHierarchyLevel === MarketingHierarchyLevel.AD) {
          queryClient.invalidateQueries({
            queryKey: ["ads"],
          });
        } else if (tableHierarchyLevel === MarketingHierarchyLevel.ADGROUP) {
          queryClient.invalidateQueries({
            queryKey: ["adGroups"],
          });
        } else if (tableHierarchyLevel === MarketingHierarchyLevel.CAMPAIGN) {
          queryClient.invalidateQueries({
            queryKey: ["campaigns"],
          });
        } else if (tableHierarchyLevel === MarketingHierarchyLevel.ACCOUNT) {
          queryClient.invalidateQueries({
            queryKey: ["accounts"],
          });
        }
      },
    }),
  );
}

interface BulkEditParams {
  dataFilters: TemporalDataFilter & TagsDataFilter & AdNetworkFilter;
  includedResources: number[];
  entityId: number | undefined;
  routeHierarchyLevel?: MarketingHierarchyLevel;
  tableHierarchyLevel: MarketingHierarchyLevel;
  tagsToAdd: Tag[];
  tagsToRemove: Tag[];
  search?: string;
  filterCriteria?: MetricFilterSet[];
  // Must mirror the table's "Active only" filter: "apply to all pages"
  // targets exactly the rows the user is looking at, so leaving this out
  // would tag paused/ended/archived rows they never saw.
  serving?: boolean;
}

function bulkEditMarketingPerformanceTags({
  dataFilters,
  includedResources,
  entityId,
  routeHierarchyLevel,
  tableHierarchyLevel,
  tagsToAdd,
  tagsToRemove,
  search,
  filterCriteria,
  serving,
}: BulkEditParams): Promise<void> {
  const formattedFilterCriteria = filterCriteriaFormatter(filterCriteria);
  let hierarchyEndpoint: string;
  if (tableHierarchyLevel === MarketingHierarchyLevel.AD) {
    hierarchyEndpoint = "ads";
  } else if (tableHierarchyLevel === MarketingHierarchyLevel.ADGROUP) {
    hierarchyEndpoint = "ad_groups";
  } else if (tableHierarchyLevel === MarketingHierarchyLevel.ACCOUNT) {
    hierarchyEndpoint = "accounts";
  } else {
    hierarchyEndpoint = "campaigns";
  }

  const campaign =
    routeHierarchyLevel === MarketingHierarchyLevel.CAMPAIGN
      ? entityId
      : undefined;
  const adGroup =
    routeHierarchyLevel === MarketingHierarchyLevel.ADGROUP
      ? entityId
      : undefined;

  const selectedResourceIds =
    routeHierarchyLevel === MarketingHierarchyLevel.ACCOUNT
      ? [entityId as number]
      : includedResources;

  return api
    .post(
      `resources/marketing/${hierarchyEndpoint}/bulk_edit_tags/`,
      {
        tagsToAdd: tagsToAdd.map((tag) => tag.pk),
        tagsToRemove: tagsToRemove.map((tag) => tag.pk),
      },
      {
        params: {
          search,
          ...formattedFilterCriteria,
          ...dataFilters,
          campaign,
          adGroup,
          selectedResourceIds,
          serving,
        },
      },
    )
    .then((response) => response.data);
}

export interface UpdateGoogleAdsRSADetails {
  pk: number;
  existingUrl: string;
  newUrl?: string | null;
  existingHeadlines: string[];
  newHeadlines?: string[] | null;
  existingDescriptions: string[];
  newDescriptions?: string[] | null;
}

export function useUpdateGoogleAdsRSAContentMutation() {
  const queryClient = useQueryClient();
  return reactive(
    useMutation({
      mutationFn: updateGoogleAdsRSAContent,
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: ["ads"],
        });
      },
    }),
  );
}

export function useChangeCampaignBudgetMutation() {
  const queryClient = useQueryClient();
  return reactive(
    useMutation({
      mutationFn: changeCampaignBudget,
      onSettled: () => {
        queryClient.invalidateQueries({ queryKey: ["campaigns"] });
        queryClient.invalidateQueries({ queryKey: ["campaign"] });
        queryClient.invalidateQueries({
          queryKey: ["otherCampaignsWithSameBudget"],
        });
      },
    }),
  );
}

function updateGoogleAdsRSAContent(
  payload: UpdateGoogleAdsRSADetails,
): Promise<void> {
  return api
    .post(
      `resources/marketing/ads/${payload.pk}/update_google_ads_rsa_content/`,
      payload,
    )
    .then((response) => response.data);
}

function changeCampaignBudget(payload: {
  campaignId: number;
  newAmount: number;
  existingAmount: number;
  existingType: BudgetType;
  existingCurrency: string;
  existingIsShared: boolean;
  existingOtherCampaignsWithSameBudget: string[];
  force: boolean;
}) {
  return api.post(
    `resources/marketing/campaigns/${payload.campaignId}/change_budget/`,
    payload,
  );
}

export function useChangeAdGroupBudgetMutation() {
  const queryClient = useQueryClient();
  return reactive(
    useMutation({
      mutationFn: changeAdGroupBudget,
      onSettled: () => {
        queryClient.invalidateQueries({ queryKey: ["adGroups"] });
        queryClient.invalidateQueries({ queryKey: ["adGroup"] });
      },
    }),
  );
}

function changeAdGroupBudget(payload: {
  adGroupId: number;
  newAmount: number;
  existingAmount: number;
  existingType: BudgetType;
  existingCurrency: string;
  force: boolean;
}) {
  return api.post(
    `resources/marketing/ad_groups/${payload.adGroupId}/change_budget/`,
    payload,
  );
}

function listOtherCampaignsWithSameBudget({
  queryKey: [, { pk }],
}: QueryFunctionContext<{
  pk: number;
}>): Promise<{ pk: number; name: string; externalId: string }[]> {
  return api
    .get(
      `/resources/marketing/campaigns/${pk}/other_campaigns_with_same_budget/`,
    )
    .then((response) => response.data);
}

export function useOtherCampaignsWithSameBudgetQuery({
  pk,
  enabled,
}: {
  pk: Ref<number | undefined>;
  enabled?: Ref<boolean>;
}) {
  const queryKey = [
    "otherCampaignsWithSameBudget",
    { pk: pk as Ref<number> },
  ] as const;
  return reactive(
    useQuery({
      queryKey,
      queryFn: listOtherCampaignsWithSameBudget,
      enabled: computed(
        () => pk.value !== undefined && (enabled?.value ?? true),
      ),
      staleTime: MARKETING_STALE_TIME,
      gcTime: MARKETING_GC_TIME,
    }),
  );
}

/**
 * Checks if a SILO/NAGR metric is unavailable at the given hierarchy level.
 *
 * Mostly Google Ads specific metrics (at least for now).
 */
export function isSiloMetricUnavailable(
  metric: MarketingTableMetricKey,
  level: MarketingHierarchyLevel,
): boolean {
  // These metrics aren't available at AD level.
  if (
    level === MarketingHierarchyLevel.AD &&
    (metric === "absoluteTopImpressionPercentage" ||
      metric === "topImpressionPercentage" ||
      metric === "searchImpressionShare" ||
      metric === "searchRankLostImpressionShare" ||
      metric === "searchBudgetLostImpressionShare")
  ) {
    return true;
  }

  // These aren't available at SEARCH_TERM level.
  if (
    level === MarketingHierarchyLevel.SEARCH_TERM &&
    (metric === "searchImpressionShare" ||
      metric === "searchRankLostImpressionShare")
  ) {
    return true;
  }

  // searchBudgetLostImpressionShare only available at account and campaign levels.
  if (
    (level === MarketingHierarchyLevel.ADGROUP ||
      level === MarketingHierarchyLevel.KEYWORD ||
      level === MarketingHierarchyLevel.SEARCH_TERM) &&
    metric === "searchBudgetLostImpressionShare"
  ) {
    return true;
  }

  // historicalQualityScore only available at keyword level.
  if (
    level !== MarketingHierarchyLevel.KEYWORD &&
    metric === "historicalQualityScore"
  ) {
    return true;
  }

  return false;
}

/**
 * Checks if a metric is a SILO/NAGR (non-aggregatable) metric.
 * These metrics require live data fetching and cannot be aggregated from cached data.
 */
export function isSiloMetric(metric: MarketingTableMetricKey): boolean {
  return metric === "qualityScore" || isNagrMetric(metric);
}
